import React, { createContext, useContext, useReducer, useCallback, useEffect } from "react";
import { STORAGE_KEYS, VITALS_PRIORITY_EXCEPTION_TYPES } from "../constants";
import { fetchCategoryGroups, fetchAllCategories } from "../apis/questionnaire";
import { logSecretly } from "../utils/logSecretly";
import { mergeDeviceMeasures, mergeDeviceVisualMedia, mergeMeasurementsByName } from "../utils/vitalsPriority";
import { useLocalization } from "./LocalizationContext";
import { useAuth } from "./AuthContext";

const VALID_AI_LANGUAGES = new Set(["ar", "en"]);
const DEFAULT_AI_LANGUAGE = "ar";

const getPersistedAiLanguage = () => {
  try {
    const savedLanguage = localStorage.getItem(STORAGE_KEYS.AI_LANGUAGE);
    if (savedLanguage && VALID_AI_LANGUAGES.has(savedLanguage)) {
      return savedLanguage;
    }
  } catch (error) {
    logSecretly("Unable to read AI language from localStorage:", error);
  }

  return DEFAULT_AI_LANGUAGE;
};

const createInitialState = () => ({
  userData: null,
  vitalsData: null,
  consultationData: null,
  podData: null,
  isLoading: false,
  error: null,
  currentCategory: null,
  completedCategories: [],
  categoryGroups: [],
  categories: [],
  currentGroupType: null,
  isLoadingCategories: false,
  categoriesError: null,
  savedAnswers: {}, // Store answers for each assessment: { assessmentId: { questionId: answer } }
  assessmentProgress: {}, // Track question index per assessment: { assessmentId: questionIndex }
  measures: null, // Store vitals measures (flattened across devices for multi-device pods)
  measuresByDevice: { HIGO: [], LEKA: [], OMRON: [] }, // Per-device vitals slices for "new flow" pods
  latestVitalsSource: {}, // measurementType -> device that most recently reported it, for VITALS_PRIORITY_EXCEPTION_TYPES ("latest event wins" types)
  visualMediaByDevice: { HIGO: {}, LEKA: {}, OMRON: {} }, // Per-device, per-measurementType media (images/audio) for "new flow" pods
  visualMedia: [], // Store visual media entries (flattened across devices for multi-device pods)
  ecgImageUrl: null, // Store ECG image URL
  aiLanguage: getPersistedAiLanguage(), // AI assistant language preference (device-persisted)
  completedSteps: {}, // Track completed dashboard steps: { vitals: true, consultation: true, ... }
});

// Initial state
const initialState = createInitialState();

// Action types
export const ACTION_TYPES = {
  SET_USER_DATA: "SET_USER_DATA",
  SET_VITALS_DATA: "SET_VITALS_DATA",
  SET_CONSULTATION_DATA: "SET_CONSULTATION_DATA",
  SET_POD_DATA: "SET_POD_DATA",
  SET_LOADING: "SET_LOADING",
  SET_ERROR: "SET_ERROR",
  RESET_STATE: "RESET_STATE",
  SET_CURRENT_CATEGORY: "SET_CURRENT_CATEGORY",
  SET_COMPLETED_CATEGORIES: "SET_COMPLETED_CATEGORIES",
  SET_CATEGORY_GROUPS: "SET_CATEGORY_GROUPS",
  SET_CATEGORIES: "SET_CATEGORIES",
  SET_CURRENT_GROUP: "SET_CURRENT_GROUP",
  SET_LOADING_CATEGORIES: "SET_LOADING_CATEGORIES",
  SET_CATEGORIES_ERROR: "SET_CATEGORIES_ERROR",
  SAVE_ANSWER: "SAVE_ANSWER",
  CLEAR_ANSWERS: "CLEAR_ANSWERS",
  SET_ASSESSMENT_PROGRESS: "SET_ASSESSMENT_PROGRESS",
  SET_MEASURES: "SET_MEASURES",
  SET_DEVICE_MEASURES: "SET_DEVICE_MEASURES",
  SET_VISUAL_MEDIA: "SET_VISUAL_MEDIA",
  SET_ECG_IMAGE_URL: "SET_ECG_IMAGE_URL",
  SET_AI_LANGUAGE: "SET_AI_LANGUAGE",
  MARK_STEP_COMPLETED: "MARK_STEP_COMPLETED",
};

// Reducer
const appReducer = (state, action) => {
  switch (action.type) {
    case ACTION_TYPES.SET_USER_DATA:
      return {
        ...state,
        userData: action.payload,
      };

    case ACTION_TYPES.SET_VITALS_DATA:
      return {
        ...state,
        vitalsData: action.payload,
      };

    case ACTION_TYPES.SET_CONSULTATION_DATA:
      return {
        ...state,
        consultationData: action.payload,
      };

    case ACTION_TYPES.SET_POD_DATA:
      return {
        ...state,
        podData: action.payload,
      };

    case ACTION_TYPES.SET_LOADING:
      return {
        ...state,
        isLoading: action.payload,
      };

    case ACTION_TYPES.SET_ERROR:
      return {
        ...state,
        error: action.payload,
      };

    case ACTION_TYPES.RESET_STATE:
      return createInitialState();

    case ACTION_TYPES.SET_CURRENT_CATEGORY:
      return {
        ...state,
        currentCategory: action.payload,
      };

    case ACTION_TYPES.SET_COMPLETED_CATEGORIES:
      return {
        ...state,
        completedCategories: action.payload,
      };
    case ACTION_TYPES.SET_CATEGORY_GROUPS:
      return {
        ...state,
        categoryGroups: action.payload,
      };

    case ACTION_TYPES.SET_CURRENT_GROUP:
      return {
        ...state,
        currentGroupType: action.payload,
      };

    case ACTION_TYPES.SET_CATEGORIES:
      return {
        ...state,
        categories: action.payload,
      };

    case ACTION_TYPES.SET_LOADING_CATEGORIES:
      return {
        ...state,
        isLoadingCategories: action.payload,
      };

    case ACTION_TYPES.SET_CATEGORIES_ERROR:
      return {
        ...state,
        categoriesError: action.payload,
      };

    case ACTION_TYPES.SAVE_ANSWER:
      const { assessmentId, questionId, answer } = action.payload;
      return {
        ...state,
        savedAnswers: {
          ...state.savedAnswers,
          [assessmentId]: {
            ...state.savedAnswers[assessmentId],
            [questionId]: answer,
          },
        },
      };

    case ACTION_TYPES.CLEAR_ANSWERS:
      const { assessmentId: clearAssessmentId } = action.payload;
      const newSavedAnswers = { ...state.savedAnswers };
      delete newSavedAnswers[clearAssessmentId];
      return {
        ...state,
        savedAnswers: newSavedAnswers,
      };

    case ACTION_TYPES.SET_ASSESSMENT_PROGRESS:
      return {
        ...state,
        assessmentProgress: {
          ...state.assessmentProgress,
          [action.payload.assessmentId]: action.payload.questionIndex,
        },
      };

    case ACTION_TYPES.SET_MEASURES:
      return {
        ...state,
        measures: action.payload,
      };

    case ACTION_TYPES.SET_DEVICE_MEASURES: {
      const { device, measurements } = action.payload;
      const measuresByDevice = {
        ...state.measuresByDevice,
        [device]: mergeMeasurementsByName(state.measuresByDevice[device], measurements),
      };
      const latestVitalsSource = { ...state.latestVitalsSource };
      measurements.forEach((measure) => {
        if (VITALS_PRIORITY_EXCEPTION_TYPES.includes(measure.name)) {
          latestVitalsSource[measure.name] = device;
        }
      });
      return {
        ...state,
        measuresByDevice,
        latestVitalsSource,
        measures: mergeDeviceMeasures(measuresByDevice, state.podData?.name, latestVitalsSource),
      };
    }

    case ACTION_TYPES.SET_VISUAL_MEDIA: {
      const { device, measurementType, media } = action.payload;
      const deviceMedia = { ...(state.visualMediaByDevice[device] || {}), [measurementType]: media };
      const visualMediaByDevice = { ...state.visualMediaByDevice, [device]: deviceMedia };
      return {
        ...state,
        visualMediaByDevice,
        visualMedia: mergeDeviceVisualMedia(visualMediaByDevice, state.podData?.name),
      };
    }

    case ACTION_TYPES.SET_ECG_IMAGE_URL:
      return {
        ...state,
        ecgImageUrl: action.payload,
      };

    case ACTION_TYPES.SET_AI_LANGUAGE:
      return {
        ...state,
        aiLanguage: action.payload,
      };

    case ACTION_TYPES.MARK_STEP_COMPLETED:
      return {
        ...state,
        completedSteps: { ...state.completedSteps, [action.payload]: true },
      };

    default:
      return state;
  }
};

// Create context
const AppContext = createContext();

// Provider component
export const AppProvider = ({ children }) => {
  const [state, dispatch] = useReducer(appReducer, initialState);
  const { language } = useLocalization();
  const { clearSession } = useAuth();

  const setUserData = (userData) => {
    dispatch({ type: ACTION_TYPES.SET_USER_DATA, payload: userData });
  };

  const setVitalsData = (vitalsData) => {
    dispatch({ type: ACTION_TYPES.SET_VITALS_DATA, payload: vitalsData });
  };

  const setConsultationData = (consultationData) => {
    dispatch({
      type: ACTION_TYPES.SET_CONSULTATION_DATA,
      payload: consultationData,
    });
  };

  const setPodData = (podData) => {
    dispatch({
      type: ACTION_TYPES.SET_POD_DATA,
      payload: podData,
    });
  };

  const setLoading = (isLoading) => {
    dispatch({ type: ACTION_TYPES.SET_LOADING, payload: isLoading });
  };

  const setError = (error) => {
    dispatch({ type: ACTION_TYPES.SET_ERROR, payload: error });
  };

  const resetState = () => {
    dispatch({ type: ACTION_TYPES.RESET_STATE });
    clearSession();
  };

  const setCurrentCategory = (currentCategory) => {
    dispatch({
      type: ACTION_TYPES.SET_CURRENT_CATEGORY,
      payload: currentCategory,
    });
  };

  const setCurrentGroup = (groupType) => {
    dispatch({
      type: ACTION_TYPES.SET_CURRENT_GROUP,
      payload: groupType,
    });
  };

  const setCompletedCategories = (completedCategories) => {
    dispatch({
      type: ACTION_TYPES.SET_COMPLETED_CATEGORIES,
      payload: completedCategories,
    });
  };

  const setCategories = (categories) => {
    dispatch({ type: ACTION_TYPES.SET_CATEGORIES, payload: categories });
  };

  const loadCategoryGroups = useCallback(async () => {
    if (state.isLoadingCategories) return;

    try {
      dispatch({ type: ACTION_TYPES.SET_LOADING_CATEGORIES, payload: true });
      dispatch({ type: ACTION_TYPES.SET_CATEGORIES_ERROR, payload: null });

      const groups = await fetchCategoryGroups();
      dispatch({ type: ACTION_TYPES.SET_CATEGORY_GROUPS, payload: groups });
      // Derive flat list so AssessmentDetail can look up individual assessments by id
      const flatCategories = groups.flatMap(g => g.assessments);
      dispatch({ type: ACTION_TYPES.SET_CATEGORIES, payload: flatCategories });
    } catch (err) {
      logSecretly("Error loading category groups in context:", err);
      dispatch({ type: ACTION_TYPES.SET_CATEGORIES_ERROR, payload: "Failed to load assessments" });
    } finally {
      dispatch({ type: ACTION_TYPES.SET_LOADING_CATEGORIES, payload: false });
    }
  }, [state.isLoadingCategories]);

  // Keep backward-compatible alias
  const loadCategories = loadCategoryGroups;

  // Refetch categories when language changes
  useEffect(() => {
    if (state.categoryGroups.length > 0) {
      loadCategoryGroups();
    }
  }, [language]);

  const saveAnswer = (assessmentId, questionId, answer) => {
    dispatch({
      type: ACTION_TYPES.SAVE_ANSWER,
      payload: { assessmentId, questionId, answer },
    });
  };

  const clearAnswers = (assessmentId) => {
    dispatch({
      type: ACTION_TYPES.CLEAR_ANSWERS,
      payload: { assessmentId },
    });
  };

  const setMeasures = (measures) => {
    dispatch({
      type: ACTION_TYPES.SET_MEASURES,
      payload: measures,
    });
  };

  const setDeviceMeasures = (device, measurements) => {
    dispatch({
      type: ACTION_TYPES.SET_DEVICE_MEASURES,
      payload: { device, measurements },
    });
  };

  const setVisualMedia = (device, measurementType, media) => {
    dispatch({
      type: ACTION_TYPES.SET_VISUAL_MEDIA,
      payload: { device, measurementType, media },
    });
  };

  const setEcgImageUrl = (ecgImageUrl) => {
    dispatch({
      type: ACTION_TYPES.SET_ECG_IMAGE_URL,
      payload: ecgImageUrl,
    });
  };

  const setAiLanguage = (lang) => {
    const normalizedLanguage = VALID_AI_LANGUAGES.has(lang) ? lang : DEFAULT_AI_LANGUAGE;
    try {
      localStorage.setItem(STORAGE_KEYS.AI_LANGUAGE, normalizedLanguage);
    } catch (error) {
      logSecretly("Unable to persist AI language to localStorage:", error);
    }

    dispatch({
      type: ACTION_TYPES.SET_AI_LANGUAGE,
      payload: normalizedLanguage,
    });
  };

  const markStepCompleted = (stepId) => {
    dispatch({
      type: ACTION_TYPES.MARK_STEP_COMPLETED,
      payload: stepId,
    });
  };

  const setAssessmentProgress = (assessmentId, questionIndex) => {
    dispatch({
      type: ACTION_TYPES.SET_ASSESSMENT_PROGRESS,
      payload: { assessmentId, questionIndex },
    });
  };

  const value = {
    ...state,
    setUserData,
    setVitalsData,
    setConsultationData,
    setPodData,
    setLoading,
    setError,
    resetState,
    setCurrentCategory,
    setCurrentGroup,
    setCompletedCategories,
    setCategories,
    loadCategories,
    loadCategoryGroups,
    saveAnswer,
    clearAnswers,
    setMeasures,
    setDeviceMeasures,
    setVisualMedia,
    setEcgImageUrl,
    setAiLanguage,
    markStepCompleted,
    setAssessmentProgress,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};

// Custom hook to use context
export const useAppContext = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error("useAppContext must be used within an AppProvider");
  }
  return context;
};
